import { useEffect, useRef, useState } from "react"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import {
  $deleteTableColumn__EXPERIMENTAL,
  $deleteTableRow__EXPERIMENTAL,
  $getTableColumnIndexFromTableCellNode,
  $getTableNodeFromLexicalNodeOrThrow,
  $getTableRowIndexFromTableCellNode,
  $insertTableColumn__EXPERIMENTAL,
  $insertTableRow__EXPERIMENTAL,
  $isTableCellNode,
  $isTableNode,
  $isTableRowNode,
  TableCellHeaderStates,
} from "@lexical/table"
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@ui/shad"
import {
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  KEY_BACKSPACE_COMMAND,
  KEY_ENTER_COMMAND,
  KEY_TAB_COMMAND,
  type LexicalNode,
} from "lexical"
import {
  ArrowDownToLine,
  ArrowLeftToLine,
  ArrowRightToLine,
  ArrowUpToLine,
  MoreHorizontal,
  MoreVertical,
  Plus,
  Trash2,
} from "lucide-react"
import { createPortal } from "react-dom"

function $findParentTableCell(node: LexicalNode | null) {
  let current: LexicalNode | null = node
  while (current && !$isTableCellNode(current)) {
    current = current.getParent()
  }
  return $isTableCellNode(current) ? current : null
}

// When `hasHorizontalScroll` is enabled on TablePlugin, Lexical's TableNode.createDOM
// returns the scroll wrapper div (not the <table>), so editor.getElementByKey returns
// the wrapper. This unwraps it so we always measure the actual <table> element.
function getTableElement(el: HTMLElement | null): HTMLElement | null {
  if (!el) return null
  if (el.tagName === "TABLE") return el
  const inner = el.querySelector("table")
  return inner instanceof HTMLElement ? inner : null
}

function getVisibleWrapperRect(wrapperEl: HTMLElement): DOMRect {
  const wrapperRect = wrapperEl.getBoundingClientRect()
  let left = wrapperRect.left
  let right = wrapperRect.right
  let top = 0
  let bottom = window.innerHeight
  let ancestor = wrapperEl.parentElement

  while (ancestor) {
    const styles = window.getComputedStyle(ancestor)
    const rect = ancestor.getBoundingClientRect()
    const clipsX = ["auto", "clip", "hidden", "scroll"].includes(styles.overflowX)
    const clipsY = ["auto", "clip", "hidden", "scroll"].includes(styles.overflowY)

    if (clipsX) {
      left = Math.max(left, rect.left)
      right = Math.min(right, rect.right)
    }
    if (clipsY) {
      top = Math.max(top, rect.top)
      bottom = Math.min(bottom, rect.bottom)
    }

    ancestor = ancestor.parentElement
  }

  return new DOMRect(left, top, Math.max(0, right - left), Math.max(0, bottom - top))
}

interface HoverState {
  tableKey: string
  tableRect: DOMRect
  // Bounding rect of the scroll wrapper around the table. Used to clip the
  // append "+" buttons so they don't render past the visible scroll viewport
  // when the table is wider than the dialog.
  wrapperRect: DOMRect
  rowKey: string
  rowRect: DOMRect
  colIndex: number
  colRect: DOMRect
  isHeaderRow: boolean
}

interface PendingDeleteState {
  rowKey: string
  rect: DOMRect
}

const BUTTON_THICKNESS = 14
const INDICATOR_BAR_SIZE = 3
const INDICATOR_HIT_SIZE = 20
const INDICATOR_SPAN = 0.8

export default function TableHoverActionsPlugin() {
  const [editor] = useLexicalComposerContext()
  const [hover, setHover] = useState<HoverState | null>(null)
  const [openMenu, setOpenMenu] = useState<"row" | "column" | null>(null)
  const [indicatorHovered, setIndicatorHovered] = useState<"row" | "column" | null>(null)
  const [pendingDelete, setPendingDelete] = useState<PendingDeleteState | null>(null)
  const pendingDeleteRef = useRef<PendingDeleteState | null>(null)
  const openMenuRef = useRef<"row" | "column" | null>(null)

  pendingDeleteRef.current = pendingDelete
  openMenuRef.current = openMenu

  // Reset indicator hover state when the cursor moves to a different cell or the menu closes,
  // so a stale "indicator hovered" doesn't keep the three-dot button visible after the wrapper
  // re-renders at a new position (since we don't get a fresh mouseenter/leave in that case).
  useEffect(() => {
    setIndicatorHovered(null)
  }, [hover?.rowKey, hover?.colIndex, openMenu])

  useEffect(() => {
    const isEmptyRect = (rect: DOMRect) => rect.width === 0 && rect.height === 0

    // Compute the hover state from the current editor selection. Returns null if the
    // selection isn't inside a table cell, or `prev` if the DOM isn't ready yet.
    const computeHoverFromSelection = (prev: HoverState | null): HoverState | null => {
      let result: HoverState | null = null
      editor.getEditorState().read(() => {
        const selection = $getSelection()
        if (!$isRangeSelection(selection)) return
        const cell = $findParentTableCell(selection.anchor.getNode())
        if (!cell) return

        const tableNode = $getTableNodeFromLexicalNodeOrThrow(cell)
        const tableKey = tableNode.getKey()
        const rowNode = cell.getParent()
        if (!$isTableRowNode(rowNode)) return
        const rowKey = rowNode.getKey()
        const colIndex = $getTableColumnIndexFromTableCellNode(cell)
        const firstCellOfRow = rowNode.getChildren()[0]
        const isHeaderRow = $isTableCellNode(firstCellOfRow) && firstCellOfRow.hasHeaderState(TableCellHeaderStates.ROW)

        const tableElRaw = editor.getElementByKey(tableKey)
        const tableEl = getTableElement(tableElRaw instanceof HTMLElement ? tableElRaw : null)
        const rowEl = editor.getElementByKey(rowKey)
        if (!tableEl || !(rowEl instanceof HTMLElement)) {
          result = prev
          return
        }

        const tableRect = tableEl.getBoundingClientRect()
        if (isEmptyRect(tableRect)) {
          result = prev
          return
        }

        // The scroll wrapper is the table's parent element when hasHorizontalScroll is on,
        // otherwise it's the table itself.
        const wrapperEl = tableEl.parentElement instanceof HTMLElement ? tableEl.parentElement : null
        const wrapperRect = wrapperEl ? getVisibleWrapperRect(wrapperEl) : tableRect

        const rowRect = rowEl.getBoundingClientRect()
        const cellDom = rowEl.children[colIndex]
        const cellRect =
          cellDom instanceof HTMLElement
            ? cellDom.getBoundingClientRect()
            : new DOMRect(tableRect.left, tableRect.top, 0, tableRect.height)
        const colRect = new DOMRect(cellRect.left, tableRect.top, cellRect.width, tableRect.height)

        result = {
          tableKey,
          tableRect,
          wrapperRect,
          rowKey,
          rowRect,
          colIndex,
          colRect,
          isHeaderRow,
        }
      })
      return result
    }

    const unregisterEnter = editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event) => {
        if (event && (event.shiftKey || event.metaKey || event.ctrlKey || event.altKey)) return false
        const selection = $getSelection()
        if (!$isRangeSelection(selection)) return false
        const cell = $findParentTableCell(selection.anchor.getNode())
        if (!cell) return false
        const tableNode = $getTableNodeFromLexicalNodeOrThrow(cell)
        const rows = tableNode.getChildren()
        const rowIndex = $getTableRowIndexFromTableCellNode(cell)
        const colIndex = $getTableColumnIndexFromTableCellNode(cell)
        const lastRowIndex = rows.length - 1

        event?.preventDefault()

        if (rowIndex === lastRowIndex) {
          // Last row: insert a new row and move into it.
          $insertTableRow__EXPERIMENTAL(true)
          const newRow = tableNode.getChildren()[lastRowIndex + 1]
          if ($isTableRowNode(newRow)) {
            const firstCell = newRow.getChildren()[0]
            if ($isTableCellNode(firstCell)) firstCell.selectStart()
          }
          return true
        }

        // Otherwise: move to the cell directly below (same column, next row).
        const nextRow = rows[rowIndex + 1]
        if ($isTableRowNode(nextRow)) {
          const nextCell = nextRow.getChildren()[colIndex]
          if ($isTableCellNode(nextCell)) nextCell.selectStart()
        }
        return true
      },
      COMMAND_PRIORITY_HIGH
    )

    const unregisterBackspace = editor.registerCommand(
      KEY_BACKSPACE_COMMAND,
      (event) => {
        const selection = $getSelection()
        if (!$isRangeSelection(selection)) return false
        if (!selection.isCollapsed()) return false
        const cell = $findParentTableCell(selection.anchor.getNode())
        if (!cell) return false
        if (cell.getTextContentSize() !== 0) return false

        const row = cell.getParent()
        if (!$isTableRowNode(row)) return false
        const rowKey = row.getKey()

        const existing = pendingDeleteRef.current
        if (existing && existing.rowKey === rowKey) {
          cell.selectStart()
          $deleteTableRow__EXPERIMENTAL()
          setPendingDelete(null)
          event?.preventDefault()
          return true
        }

        const rowEl = editor.getElementByKey(rowKey)
        if (!rowEl) return false
        setPendingDelete({ rowKey, rect: rowEl.getBoundingClientRect() })
        event?.preventDefault()
        return true
      },
      COMMAND_PRIORITY_HIGH
    )

    const unregisterTab = editor.registerCommand(
      KEY_TAB_COMMAND,
      (event) => {
        if (event && (event.metaKey || event.ctrlKey || event.altKey)) return false
        const selection = $getSelection()
        if (!$isRangeSelection(selection)) return false
        const cell = $findParentTableCell(selection.anchor.getNode())
        if (!cell) return false

        const tableNode = $getTableNodeFromLexicalNodeOrThrow(cell)
        const rows = tableNode.getChildren()
        const firstRow = rows[0]
        if (!$isTableRowNode(firstRow)) return false

        const colIndex = $getTableColumnIndexFromTableCellNode(cell)
        const rowIndex = $getTableRowIndexFromTableCellNode(cell)
        const lastColIndex = firstRow.getChildren().length - 1

        event?.preventDefault()

        if (event?.shiftKey) {
          // Shift+Tab: previous cell, wrapping to the last cell of the previous row.
          if (colIndex > 0) {
            const row = rows[rowIndex]
            if ($isTableRowNode(row)) {
              const prev = row.getChildren()[colIndex - 1]
              if ($isTableCellNode(prev)) prev.selectEnd()
            }
          } else if (rowIndex > 0) {
            const prevRow = rows[rowIndex - 1]
            if ($isTableRowNode(prevRow)) {
              const prev = prevRow.getChildren().at(-1)
              if ($isTableCellNode(prev)) prev.selectEnd()
            }
          }
          return true
        }

        // Tab: next cell, wrapping to the first cell of the next row.
        if (colIndex < lastColIndex) {
          const row = rows[rowIndex]
          if ($isTableRowNode(row)) {
            const next = row.getChildren()[colIndex + 1]
            if ($isTableCellNode(next)) next.selectStart()
          }
        } else if (rowIndex < rows.length - 1) {
          const nextRow = rows[rowIndex + 1]
          if ($isTableRowNode(nextRow)) {
            const next = nextRow.getChildren()[0]
            if ($isTableCellNode(next)) next.selectStart()
          }
        }
        return true
      },
      COMMAND_PRIORITY_HIGH
    )

    const onOtherKey = (event: KeyboardEvent) => {
      if (event.key !== "Backspace") setPendingDelete(null)
    }
    const onMouseDown = () => setPendingDelete(null)

    document.addEventListener("keydown", onOtherKey, true)
    document.addEventListener("mousedown", onMouseDown, true)

    // Drive the hover state off the editor selection. Fires on every Lexical
    // update (typing, selection change, insert/delete), so the affordances
    // always follow wherever the cursor is inside a table.
    let animationFrame: number | null = null
    const runUpdate = () => {
      if (animationFrame !== null) return

      animationFrame = requestAnimationFrame(() => {
        animationFrame = null
        setHover((prev) => {
          // Don't reposition/clear while a dropdown menu is open — the user is
          // interacting with it and the underlying selection may not be in the table.
          if (openMenuRef.current) return prev
          return computeHoverFromSelection(prev)
        })
        setPendingDelete((prev) => {
          if (!prev) return prev
          const rowEl = editor.getElementByKey(prev.rowKey)
          if (!rowEl) return null
          const rect = rowEl.getBoundingClientRect()
          if (isEmptyRect(rect)) return prev
          return { rowKey: prev.rowKey, rect }
        })
      })
    }

    const unregister = editor.registerUpdateListener(runUpdate)
    const rootElement = editor.getRootElement()
    const resizeObserver = rootElement ? new ResizeObserver(runUpdate) : null
    if (rootElement) resizeObserver?.observe(rootElement)
    window.addEventListener("resize", runUpdate)
    window.addEventListener("scroll", runUpdate, true)
    // Run once on mount in case the selection is already inside a table.
    runUpdate()

    return () => {
      document.removeEventListener("keydown", onOtherKey, true)
      document.removeEventListener("mousedown", onMouseDown, true)
      unregister()
      unregisterEnter()
      unregisterTab()
      unregisterBackspace()
      resizeObserver?.disconnect()
      if (animationFrame !== null) cancelAnimationFrame(animationFrame)
      window.removeEventListener("resize", runUpdate)
      window.removeEventListener("scroll", runUpdate, true)
    }
  }, [editor])

  if (!hover && !pendingDelete) return null

  const tableRect = hover?.tableRect
  const wrapperRect = hover?.wrapperRect
  const rowRect = hover?.rowRect
  const colRect = hover?.colRect

  // After any editor.update(), the editor update listener will re-run computeHoverFromSelection
  // and pick up fresh rects from the new selection — no manual refresh needed here.

  const runRowAction = (action: "above" | "below" | "delete") => {
    if (!hover) return
    const { tableKey, rowKey } = hover
    editor.update(() => {
      const tableNode = $getNodeByKey(tableKey)
      if (!$isTableNode(tableNode)) return
      const rowNode = $getNodeByKey(rowKey)
      if (!$isTableRowNode(rowNode)) return
      const firstCell = rowNode.getChildren()[0]
      if (!$isTableCellNode(firstCell)) return
      firstCell.selectStart()
      if (action === "above") $insertTableRow__EXPERIMENTAL(false)
      else if (action === "below") $insertTableRow__EXPERIMENTAL(true)
      else $deleteTableRow__EXPERIMENTAL()
    })
    setOpenMenu(null)
  }

  const runColumnAction = (action: "left" | "right" | "delete") => {
    if (!hover) return
    const { tableKey, colIndex } = hover
    editor.update(() => {
      const tableNode = $getNodeByKey(tableKey)
      if (!$isTableNode(tableNode)) return
      const firstRow = tableNode.getChildren()[0]
      if (!$isTableRowNode(firstRow)) return
      const targetCell = firstRow.getChildren()[colIndex]
      if (!$isTableCellNode(targetCell)) return
      targetCell.selectStart()
      if (action === "left") $insertTableColumn__EXPERIMENTAL(false)
      else if (action === "right") $insertTableColumn__EXPERIMENTAL(true)
      else $deleteTableColumn__EXPERIMENTAL()
    })
    setOpenMenu(null)
  }

  const insertRowAppend = () => {
    if (!hover) return
    const tableKey = hover.tableKey
    editor.update(() => {
      const tableNode = $getNodeByKey(tableKey)
      if (!$isTableNode(tableNode)) return
      const lastRow = tableNode.getChildren().at(-1)
      if (!$isTableRowNode(lastRow)) return
      const firstCell = lastRow.getChildren()[0]
      if (!$isTableCellNode(firstCell)) return
      firstCell.selectStart()
      $insertTableRow__EXPERIMENTAL(true)
    })
  }

  const insertColumnAppend = () => {
    if (!hover) return
    const tableKey = hover.tableKey
    editor.update(() => {
      const tableNode = $getNodeByKey(tableKey)
      if (!$isTableNode(tableNode)) return
      const firstRow = tableNode.getChildren()[0]
      if (!$isTableRowNode(firstRow)) return
      const lastCell = firstRow.getChildren().at(-1)
      if (!$isTableCellNode(lastCell)) return
      lastCell.selectStart()
      $insertTableColumn__EXPERIMENTAL(true)
    })
  }

  // Radix Dialog sets `pointer-events: none` on document.body while open, which
  // would otherwise make these buttons unclickable. Re-enable explicitly.
  const buttonStyle: React.CSSProperties = { pointerEvents: "auto" }

  return createPortal(
    <>
      {hover && tableRect && wrapperRect && rowRect && colRect && (
        <>
          {(() => {
            // Hug the table for size, but clip to the visible scroll wrapper so the
            // buttons don't render past the dialog when the table overflows. Both
            // buttons follow the same rule: show whenever any part of the table edge
            // they hug is visible inside the wrapper.
            const rowLeft = Math.max(tableRect.left, wrapperRect.left)
            const rowRight = Math.min(tableRect.right, wrapperRect.right)
            const rowWidth = Math.max(0, rowRight - rowLeft)
            const colLeft = tableRect.left + tableRect.width
            const visibleTop = Math.max(tableRect.top, wrapperRect.top)
            const visibleBottom = Math.min(tableRect.bottom, wrapperRect.bottom)
            const rowTop = tableRect.top + tableRect.height
            const showRowButton =
              rowWidth > 0 && rowTop >= wrapperRect.top && rowTop + BUTTON_THICKNESS <= wrapperRect.bottom
            const showColButton =
              colLeft <= wrapperRect.right && colLeft >= wrapperRect.left && visibleBottom > visibleTop
            return (
              <>
                {/* Append-row "+" button: spans the visible portion of the table, hugging its bottom. */}
                {showRowButton && (
                  <button
                    type="button"
                    onClick={insertRowAppend}
                    className="bg-tertiary hover:bg-blue-background border-border text-icon fixed z-[9999] flex items-center justify-center rounded border"
                    style={{
                      ...buttonStyle,
                      top: rowTop,
                      left: rowLeft,
                      width: rowWidth,
                      height: BUTTON_THICKNESS,
                    }}
                    aria-label="Add row"
                    title="Add row"
                  >
                    <Plus className="size-3" />
                  </button>
                )}
                {/* Append-column "+" button: hugs the table's right edge if it's visible. */}
                {showColButton && (
                  <button
                    type="button"
                    onClick={insertColumnAppend}
                    className="bg-tertiary hover:bg-blue-background border-border text-icon fixed z-[9999] flex items-center justify-center rounded border"
                    style={{
                      ...buttonStyle,
                      top: visibleTop,
                      left: colLeft,
                      width: BUTTON_THICKNESS,
                      height: visibleBottom - visibleTop,
                    }}
                    aria-label="Add column"
                    title="Add column"
                  >
                    <Plus className="size-3" />
                  </button>
                )}
              </>
            )
          })()}
          {/* Row indicator + actions menu — thin bar on row's left border; reveals three-dot button on hover */}
          {(() => {
            const rowActive = indicatorHovered === "row" || openMenu === "row"
            const visibleRowTop = Math.max(rowRect.top, wrapperRect.top)
            const visibleRowBottom = Math.min(rowRect.bottom, wrapperRect.bottom)
            const visibleRowHeight = Math.max(0, visibleRowBottom - visibleRowTop)
            const indicatorLength = visibleRowHeight * INDICATOR_SPAN
            const spanHeight = indicatorLength
            const spanTop = visibleRowTop + (visibleRowHeight - spanHeight) / 2
            // Hide the row indicator when the row's left edge is scrolled past the
            // visible scroll wrapper — otherwise it would float outside the dialog.
            if (
              rowRect.left < wrapperRect.left ||
              rowRect.left > wrapperRect.right ||
              visibleRowHeight < INDICATOR_HIT_SIZE
            ) {
              return null
            }
            return (
              <div
                className="fixed z-[9999] flex items-center justify-center"
                style={{
                  ...buttonStyle,
                  top: spanTop,
                  left: rowRect.left - INDICATOR_HIT_SIZE / 2,
                  width: INDICATOR_HIT_SIZE,
                  height: spanHeight,
                }}
                onMouseEnter={() => setIndicatorHovered("row")}
                onMouseLeave={() => setIndicatorHovered((prev) => (prev === "row" ? null : prev))}
              >
                {!rowActive && (
                  <div className="bg-border rounded-full" style={{ width: INDICATOR_BAR_SIZE, height: "100%" }} />
                )}
                {rowActive && (
                  <DropdownMenu open={openMenu === "row"} onOpenChange={(open) => setOpenMenu(open ? "row" : null)}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="iconSm"
                        variant="ghost"
                        type="button"
                        className="bg-tertiary border-border text-icon size-[18px] rounded border"
                        aria-label="Row actions"
                        title="Row actions"
                      >
                        <MoreVertical className="size-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent side="left" align="start" onCloseAutoFocus={(e) => e.preventDefault()}>
                      {!hover.isHeaderRow && (
                        <DropdownMenuItem onClick={() => runRowAction("above")}>
                          <ArrowUpToLine />
                          Insert row above
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => runRowAction("below")}>
                        <ArrowDownToLine />
                        Insert row below
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => runRowAction("delete")}
                        className="text-red-accent focus:text-red-accent"
                      >
                        <Trash2 />
                        Delete row
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            )
          })()}
          {/* Column indicator + actions menu — thin bar on column's top border; reveals three-dot button on hover */}
          {(() => {
            const colActive = indicatorHovered === "column" || openMenu === "column"
            // Fixed length: same pixel length as the row indicator (80% of row height) so both feel consistent.
            const spanWidth = rowRect.height * INDICATOR_SPAN
            const spanLeft = colRect.left + (colRect.width - spanWidth) / 2
            const spanCenter = spanLeft + spanWidth / 2
            const spanTop = colRect.top - INDICATOR_HIT_SIZE / 2
            // Hide the column indicator when the column has scrolled past the visible
            // scroll wrapper — same rationale as the row indicator above.
            if (
              spanCenter < wrapperRect.left ||
              spanCenter > wrapperRect.right ||
              spanTop < wrapperRect.top ||
              spanTop + INDICATOR_HIT_SIZE > wrapperRect.bottom
            ) {
              return null
            }
            return (
              <div
                className="fixed z-[9999] flex items-center justify-center"
                style={{
                  ...buttonStyle,
                  top: spanTop,
                  left: spanLeft,
                  width: spanWidth,
                  height: INDICATOR_HIT_SIZE,
                }}
                onMouseEnter={() => setIndicatorHovered("column")}
                onMouseLeave={() => setIndicatorHovered((prev) => (prev === "column" ? null : prev))}
              >
                {!colActive && (
                  <div className="bg-border rounded-full" style={{ width: "100%", height: INDICATOR_BAR_SIZE }} />
                )}
                {colActive && (
                  <DropdownMenu
                    open={openMenu === "column"}
                    onOpenChange={(open) => setOpenMenu(open ? "column" : null)}
                  >
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="iconSm"
                        variant="ghost"
                        type="button"
                        className="bg-tertiary border-border text-icon size-[18px] rounded border"
                        aria-label="Column actions"
                        title="Column actions"
                      >
                        <MoreHorizontal className="size-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent side="top" align="start" onCloseAutoFocus={(e) => e.preventDefault()}>
                      <DropdownMenuItem onClick={() => runColumnAction("left")}>
                        <ArrowLeftToLine />
                        Insert column left
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => runColumnAction("right")}>
                        <ArrowRightToLine />
                        Insert column right
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => runColumnAction("delete")}
                        className="text-red-accent focus:text-red-accent"
                      >
                        <Trash2 />
                        Delete column
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            )
          })()}
        </>
      )}
      {pendingDelete && (
        <div
          className="bg-red-background border-red-border fixed z-[9998] rounded border-2"
          style={{
            top: pendingDelete.rect.top,
            left: pendingDelete.rect.left,
            width: pendingDelete.rect.width,
            height: pendingDelete.rect.height,
            pointerEvents: "none",
          }}
        />
      )}
    </>,
    document.body
  )
}
